import type { ChangeEvent } from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppInput } from "../../../shared/components/AppInput";
import { AppLoading } from "../../../shared/components/AppLoading";
import { ModalBasic } from "../../../shared/components/ModalBasic";
import styles from "./styles.module.css";

interface Props {
  title: string;
  placeholder: string;
  isOpen: boolean;
  createEntity: (name: string) => void;
  closeModal: () => void;
  isInputDisabled: boolean;
  extraElement?: React.ReactNode;
  isLoading?: boolean;
}

export const CreateEntityModal = (props: Props) => {
  const [entityName, setEntityName] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  const handleEntityNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEntityName(event.target.value);
  };

  useLayoutEffect(() => {
    if (!props.isInputDisabled && inputRef.current) {
      requestAnimationFrame(() => {
        if (inputRef.current && !inputRef.current.disabled) {
          inputRef.current.focus();
        }
      });
    }
  }, [props.isInputDisabled]);

  return (
    <ModalBasic
      open={props.isOpen}
      title={props.title}
      hasCloseButton
      onClose={props.closeModal}
      closeModal={props.closeModal}
      hasFooter
      buttonOkText={t("versioning.save_modal_ok_button")}
      onOk={() => {
        props.createEntity(entityName as string);
        setEntityName("");
      }}
      onCancel={props.closeModal}
      className={styles["modal"]}
      disabledOkBtn={props.isLoading}
    >
      <div className={styles.container}>
        {props.isLoading && (
          <>
            <div className={styles["loading-overlay"]} />
            <AppLoading className={styles.loader} />
          </>
        )}
        {props.extraElement && props.extraElement}
        <AppInput
          inputRef={inputRef}
          value={entityName}
          onChange={handleEntityNameChange}
          placeholder={props.placeholder}
          className={styles.input}
          disabled={props.isInputDisabled}
        />
      </div>
    </ModalBasic>
  );
};
